import {
  AbsenceStatus,
  AbsenceStatusCombobox,
  AbsenceTypeCombobox,
  Button,
  Card,
  Checkbox,
  DatePicker,
  FAIcon,
  Field,
  FieldLabel,
  HideInProduction,
  LeaderGroupCombobox,
  MonthSelect,
  OnlyInProduction,
  UserCombobox,
} from '@/components'
import { cn } from '@/lib/utils'
import {
  PERMISSIONS,
  useAdmin,
  useFormWatch,
  useLeaderGroups,
  usePermissions,
} from '@/utils'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { formatISO } from 'date-fns'
import { Activity } from 'react'
import { Controller, type UseFormReturn } from 'react-hook-form'
import z from 'zod'
import { useAbsenceMutations } from '../mutations.hooks'
import type { CreateAbsenceForm } from '../types'
import { AbsenceDialog } from './absence-dialog'
import { CreateAbsenceModal } from './CreateAbsenceModal'

export const AbsencesFiltersSchema = z.object({
  view: z.enum(['table', 'calendar']),
  users: z.string().array(),
  absenceType: z.string(),
  requestStatus: z.enum(AbsenceStatus).or(z.literal('')),
  dateRange: z.object({
    from: z.date(),
    to: z.date(),
  }),
  month: z.date(),
  leaderGroup: z.string(),
  salaryMode: z.boolean(),
})

export type AbsencesFiltersForm = z.infer<typeof AbsencesFiltersSchema>

interface Props {
  form: UseFormReturn<AbsencesFiltersForm>
}

export function AbsencesFilters({ form }: Props) {
  const translations = useTranslate({
    users: 'absences.filters.users',
    absenceType: 'absences.filters.absence-type',
    requestStatus: 'absences.filters.request-status',
    dateRange: 'absences.filters.date-range',
    month: 'absences.filters.month',
    leaderGroup: 'absences.filters.leader-group',
    salaryMode: 'absences.filters.salary-mode',

    registerAbsence: 'absences.absence-dialog.add',
  })

  const view = useFormWatch(form, 'view')

  const { hasPermissions } = usePermissions()
  const canViewOthersAbsence = hasPermissions(
    PERMISSIONS.timesheets.view.othersabsence,
    false
  )
  const canAddAbsence = hasPermissions(
    PERMISSIONS.timesheets.add.absence,
    false
  )
  const canAddOthersAbsence = hasPermissions(
    PERMISSIONS.timesheets.add.othersabsence,
    false
  )
  const canViewSalaryMode = hasPermissions(
    PERMISSIONS.salary.view.salaryMode,
    false
  )
  const leaderGroups = useLeaderGroups()

  /* REMOVE BELOW WHEN NEW ABSENCE FLOW IS READY */
  const {
    mutations: { create: createAbsence },
  } = useAbsenceMutations()

  const addPrompt = usePrompt()
  const admin = useAdmin()
  async function handleCreateAbsence() {
    const { data } = await addPrompt<CreateAbsenceForm | null>(resolve => (
      <CreateAbsenceModal admin={admin} onSubmit={resolve} />
    ))

    if (!data) return

    await createAbsence({
      variables: {
        input: {
          reason: data.reason,
          absenceType: data.absenceType ?? '',
          datetimeStart: formatISO(data.dateFrom),
          datetimeEnd: formatISO(data.dateTo),
          user: data.user,
        },
      },
    })
  }
  /* REMOVE ABOVE WHEN NEW ABSENCE FLOW IS READY */

  return (
    <Card className={cn('mb-4 p-0')} contentClassName="p-0">
      <div className={cn(canAddAbsence && 'grid grid-cols-[1fr_auto]')}>
        <form className={cn('flex flex-wrap justify-start gap-4 p-4')}>
          <Activity mode={canViewOthersAbsence ? 'visible' : 'hidden'}>
            <Controller
              control={form.control}
              name="users"
              render={({ field }) => (
                <Field>
                  <FieldLabel>{translations.users}</FieldLabel>
                  <UserCombobox mode="multiple" {...field} />
                </Field>
              )}
            />
          </Activity>

          <Controller
            control={form.control}
            name="absenceType"
            render={({ field }) => (
              <Field>
                <FieldLabel>{translations.absenceType}</FieldLabel>
                <AbsenceTypeCombobox {...field} mode="single" />
              </Field>
            )}
          />

          <Controller
            control={form.control}
            name="requestStatus"
            render={({ field }) => (
              <Field>
                <FieldLabel>{translations.requestStatus}</FieldLabel>
                <AbsenceStatusCombobox {...field} mode="single" />
              </Field>
            )}
          />

          <Activity mode={view === 'table' ? 'visible' : 'hidden'}>
            <Controller
              control={form.control}
              name="dateRange"
              render={({ field }) => (
                <Field>
                  <FieldLabel>{translations.dateRange}</FieldLabel>
                  <DatePicker {...field} mode="range" format="PP" />
                </Field>
              )}
            />
          </Activity>

          <Activity mode={view === 'calendar' ? 'visible' : 'hidden'}>
            <Controller
              control={form.control}
              name="month"
              render={({ field }) => (
                <Field>
                  <FieldLabel>{translations.month}</FieldLabel>
                  <MonthSelect {...field} />
                </Field>
              )}
            />
          </Activity>

          <Activity mode={leaderGroups.hasAccess ? 'visible' : 'hidden'}>
            <Controller
              control={form.control}
              name="leaderGroup"
              render={({ field }) => (
                <Field>
                  <FieldLabel>{translations.leaderGroup}</FieldLabel>
                  <LeaderGroupCombobox {...field} mode="single" />
                </Field>
              )}
            />
          </Activity>

          <Activity mode={canViewSalaryMode ? 'visible' : 'hidden'}>
            <Controller
              control={form.control}
              name="salaryMode"
              render={({ field }) => (
                <Field className="items-center">
                  <FieldLabel>{translations.salaryMode}</FieldLabel>
                  <Checkbox {...field} />
                </Field>
              )}
            />
          </Activity>
        </form>

        {canAddAbsence && (
          <div className="flex items-end p-4 border-l border-l-gray-200">
            <OnlyInProduction>
              <Button onClick={handleCreateAbsence}>
                <FAIcon icon="plus" />
                <span>{translations.registerAbsence}</span>
              </Button>
            </OnlyInProduction>

            <HideInProduction>
              <AbsenceDialog canAddForOthers={canAddOthersAbsence} />
            </HideInProduction>
          </div>
        )}
      </div>
    </Card>
  )
}
